import { AppConfig } from '@superbet-group/web.lib.config';
import { maintenanceService } from '@superbet-group/web.lib.flags';
import { createRestClient } from '@superbet-group/web.lib.rest';
import {
    instanceGuard,
    IResponse,
} from '@superbet-group/web.lib.utilities';
import { MaintenanceType } from '@superbet-group/web.types';
import { Subscription } from 'rxjs';

import { defaultHttpTimeoutMs } from '@core/constants';
import { transformOfferEvent } from '@core/mappers/offer/transformOfferEvent';
import { transformSuperOffer } from '@core/mappers/offer/transformSuperOffer';
import { SuperOfferDto } from '@core/mappers/offer/dtos/SuperOfferDto';
import { OfferEventDto } from '@core/mappers/offer/dtos/OfferEventDto';
import OfferEvent from '@core/models/offer/OfferEvent';
import SuperOffer from '@core/models/offer/SuperOffer';
import { StructManager } from '@core/services/offer/StructManager';
import RestService from '@core/services/offer/_private/RestService';

import { StreamManager } from './_private/StreamManager';
import { SseManager } from './_private/sse/SseManager';
import Singleton from '../common/Singleton';
import SingletonError from '../common/errors/SingletonError';
import {
    SseStructure,
    SseUpdate,
} from './_private/sse/types';
import { mapMarketGroups } from '@core/mappers/struct/MarketGroupMapper';
import { MarketGroupPhase } from './_private/helpers/types';
import MarketGroupInfo from '@core/models/struct/MarketGroupInfo';
import { getOfferHost } from './helpers';
import { CategoryWithCompetitions } from '@core/models/struct/CategoryWithCompetitions';
import { transformCategoryWithCompetitions } from '@core/mappers/struct/transformCategoryWithCompetitions';
import { CategoryWithCompetitionsDto } from '@core/mappers/struct/dtos/CategoryWithCompetitionsDto';
import { getCmsUrl } from '../cms/utils';
import { GetTopEventsOptions } from './types';
import { getTopEvents } from './CmsOfferService';
import {
    getLiteMode,
    omitEvents,
} from '@modules/layout/header/utils/metaWhitelist';
import { OfferRequestParams } from './_private/models/OfferRequestParams';

/**
 * Important!
 *
 * 1. Client is obliged to call startStreaming(struct) after struct is fetched and
 * before using any other method from OfferService.
 *
 * For example:
 *
 *  - struct = await offerService.getStruct()
 *  - offerService.startStreaming(struct) // after that you can use service normally
 *  - offerService.getEvents()
 *  - offerService.subscribeToEvent()
 * ...
 *
 * 2. Client is obliged to call startStreaming(struct) (and fetch new struct) after
 * every call to stopStreaming().
 *
 * For example:
 *  - switch to casino page
 *  - call offerService.stopStreaming()
 *  - switch back to sports page
 *  - struct = await offerService.getStruct()
 *  - offerService.startStreaming(struct) // after that you can use service normally
 *  - offerService.getEvents()
 *  - offerService.subscribeToEvent()
 *
 */

class OfferService {
    private static instance?: OfferService;
    private streamManager: StreamManager;
    private restService: RestService;
    private restServiceOld: RestService;

    private hasStreamingStarted = false;

    private constructor(config: AppConfig) {
        if (OfferService.instance) {
            throw new SingletonError(this.constructor.name);
        }
        const cmsRestClient = createRestClient(
            getCmsUrl({ path: '' }),
            defaultHttpTimeoutMs,
        );
        this.restService = new RestService(
            createRestClient(
                getOfferHost(),
                defaultHttpTimeoutMs,
            ),
            cmsRestClient,
        );
        this.restServiceOld = new RestService(
            createRestClient(
                `${config.offer.hostServer}`,
                defaultHttpTimeoutMs,
            ),
            cmsRestClient,
        );
    }

    public static getInstance(): OfferService {
        return instanceGuard(OfferService.instance);
    }

    public static createInstance(config: AppConfig) {
        if (!OfferService.instance) {
            OfferService.instance = new OfferService(config);
        }
        return OfferService.instance;
    }

    /**
    * @throws {RequestError}
    * @throws {Error}
    */
    public getLiveEvents(): Promise<OfferEvent[]> {
        return this.fetchEvents(() => this.restService.getLiveEvents());
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public getEvents(startDate: Date, endDate: Date, sportId?: number): Promise<OfferEvent[]> {
        return this.fetchEvents(() => this.restService.getEvents(startDate, endDate, sportId));
    }

    public getPrematchEventsForTournaments(tournamentIds: number[]): Promise<OfferEvent[]> {
        return this.fetchEvents(() => this.restService.getPrematchEventsForTournaments(tournamentIds));
    }

    public getLiveEventsForTournaments(tournamentIds: number[]): Promise<OfferEvent[]> {
        return this.fetchEvents(() => this.restService.getLiveEventsForTournaments(tournamentIds));
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getEventsById(eventIds: number[], params?: OfferRequestParams): Promise<OfferEvent[]> {
        const matches = await this.fetchEvents(() => this.restService.getEventsById(eventIds, params));
        matches.forEach((match) => this.streamManager.setEventDetailsTimestamp?.(match));
        return matches;
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getEvent(id: number) {
        const [match] = await this.getEventsById([id]);
        return match || null;
    }

    public async getEventByBetRadarId(id: number): Promise<OfferEvent> {
        const [event] = await this.fetchEvents(() => this.restService.getEventByBetRadarId(id));
        return event || null;
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public async getSuperEvents(startDate: Date, _endDate: Date): Promise<SuperOffer> {
        const response = await this.restService.getSuperEvents(startDate);
        const superOfferDto: SuperOfferDto = response.data;
        const superOffer = transformSuperOffer(superOfferDto);

        await Promise.all([
            StructManager.getInstance().denormalizeEvents(superOffer.superExtra),
            StructManager.getInstance().denormalizeEvents(superOffer.superKvota),
            StructManager.getInstance().denormalizeEvents(superOffer.topTen),
        ]);
        superOffer.superExtra = this.removeEventsInMaintenance(superOffer.superExtra);
        superOffer.superKvota = this.removeEventsInMaintenance(superOffer.superKvota);
        superOffer.topTen = this.removeEventsInMaintenance(superOffer.topTen);
        superOffer.superExtra.forEach((match) => this.streamManager.setEventDetailsTimestamp?.(match));
        superOffer.superKvota.forEach((match) => this.streamManager.setEventDetailsTimestamp?.(match));
        superOffer.topTen.forEach((match) => this.streamManager.setEventDetailsTimestamp?.(match));
        return superOffer;
    }

    public getTopEvents(options?: Partial<GetTopEventsOptions>): Promise<OfferEvent[]> {
        return this.fetchEvents(() => getTopEvents(options));
    }

    public async getTopEventIds(): Promise<number[]> {
        const response = await this.restService.getTopEventIds();

        return response.topMatches ?? [];
    }

    public async getMarketGroups(sportId: number, phase: MarketGroupPhase): Promise<MarketGroupInfo[]> {
        const response = await this.restService.getMarketGroups(sportId, phase);
        return response.data.map((marketGroupInfo) => {
            return mapMarketGroups(marketGroupInfo);
        });
    }

    getOutrights(): Promise<OfferEvent[]> {
        return this.fetchEvents(() => this.restService.getOutrights());
    }

    public getSuperAdvantageEvents(startDate: Date, endDate: Date): Promise<OfferEvent[]> {
        return this.fetchEvents(() => this.restService.getSuperAdvantageEvents(startDate, endDate));
    }

    public getPriceBoostEvents(startDate: Date, endDate: Date): Promise<OfferEvent[]> {
        return this.fetchEvents(() => this.restService.getPriceBoostEvents(startDate, endDate));
    }

    async getCategoryWithCompetitionsBySportId(sportId: number): Promise<CategoryWithCompetitions[]> {
        const response = await this.restService.getCategoryWithCompetitionsBySportId(sportId);
        return response.data.map((dto) => transformCategoryWithCompetitions(dto as CategoryWithCompetitionsDto));
    }

    public async getDatesWithActiveOffer(sportId: number): Promise<Date[]> {
        const response = await this.restServiceOld.getDatesWithActiveOffer(sportId);
        return response.map((dateEntry) => new Date(dateEntry._id.date));
    }

    /**
     * todo we dont have any notification for subscription.  if socket isn't connected
     * todo subcription will go to queue. there is no timeout. we will see later is this is good aproach
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToEvent(
        eventId: number,
        callback: (event: SseUpdate<OfferEvent>) => void,
    ): Subscription {
        this.ensureStreamingStarted();
        return this.streamManager.subscribeToEventStream(eventId, callback);
    }

    public subscribeToStructChanges(callback: (struct: SseUpdate<Partial<SseStructure>>) => void) {
        this.ensureStreamingStarted();
        this.streamManager.subscribeToStructStream(callback);
    }

    public unsubscribeFromEvent(eventId: number) {
        this.streamManager.unsubscribeFromEventStream(eventId);
    }

    /**
     * Subscribe to stream with callback.  For every match change this method
     * will call calback with *list* of changed/new Events.
     *
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToAllEventChanges(callback: (events: SseUpdate<OfferEvent[]>) => void): Subscription {
        this.ensureStreamingStarted();
        return this.streamManager.subscribeToAllEventChangesStream(callback);
    }

    /**
     * Subscribe to stream with callback. This method will call callback for every live
     * event change.
     *
     * @return Subscription object so you can obj.unsubscribe()
     */
    public subscribeToLiveEventsCountChangesStream(callback: (eventsCount: number) => void): Subscription {
        this.ensureStreamingStarted();
        return this.streamManager.subscribeToLiveEventsCountChangesStream(callback);
    }

    public subscribeToLiveEventsCountChangesStreamOnly(callback: (eventsCount: number) => void): Subscription {
        this.ensureStreamingStarted();
        this.streamManager.startLiveEventStreaming();
        return this.streamManager.subscribeToLiveEventsCountChangesStream(callback);
    }

    /**
     * @throws {RequestError}
     * @throws {Error}
     */
    public startStreaming(): void {
        this.streamManager = new SseManager();
        this.streamManager.startStreaming();
    }

    public setRestService(restService: RestService) {
        this.restService = restService;
    }

    // eslint-disable-next-line class-methods-use-this
    public async denormalizeEvent(event: OfferEvent) {
        await StructManager.getInstance().denormalizeEvent(event);
    }

    private async fetchEvents(
        fetcher: () => Promise<IResponse<OfferEventDto[]>>,
    ) {
        const response = await fetcher();
        let matches = response.data.map(transformOfferEvent);
        await StructManager.getInstance().denormalizeEvents(matches);
        matches = this.removeEventsInMaintenance(matches);

        if (getLiteMode()) {
            matches = omitEvents(matches);
        }

        return matches;
    }

    public fetchSportsWithEvents(): Promise<number[]> {
        return this.restService.fetchSportsWithEvents();
    }

    private removeEventsInMaintenance = (events: OfferEvent[]): OfferEvent[] =>
        events.filter((e) => !isEventInMaintenance(e));

    public static clearInstance() {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete OfferService.instance;
    }

    private ensureStreamingStarted(): void {
        if (!this.hasStreamingStarted) {
            this.startStreaming();
            this.hasStreamingStarted = true;
        }
    }
}

export default OfferService as Singleton<OfferService>;

function isEventInMaintenance(event: OfferEvent) {
    return event.isOngoing() && maintenanceService.isInMaintenance(MaintenanceType.Live)
        || !event.isOngoing() && maintenanceService.isInMaintenance(MaintenanceType.Prematch);
}
